import { type ButtonProps } from "antd/lib/button";
import { Component } from "react";

import { BdsButton, BdsIconMapPin } from "@buildertrend/components";

import { type BTButtonDeprecatedType } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTModal } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTModalLayout } from "commonComponents/btWrappers/BTModal/BTModalLayout";
import { type IMapPosition, type IMark } from "commonComponents/entity/map/Map.types";
import { MapInput } from "commonComponents/entity/map/MapInput/MapInput";
import { getMapPositionFromDeviceWithFallback } from "commonComponents/entity/map/mapUtilities";

import "./MapButton.less";

interface IMapButtonProps extends Omit<ButtonProps, "type"> {
    children?: React.ReactNode;
    modalTitle: string;
    mark?: IMark;
    readOnly?: boolean;
    "data-testid": string;
    isOpenWithMap: boolean;
    entityName?: string;
    onMarkSet?: (newPosition: IMapPosition) => void;
    onMarkClear?: () => void;
    type?: BTButtonDeprecatedType;
    defaultMapStartPosition?: IMapPosition;
    onClose?: () => void;
    /**
     * Hides the built-in map-pin trigger button, leaving only the modal. The
     * modal is then opened imperatively via the `openModal` instance method.
     * Used when an external trigger (e.g. AddressActionSheet) owns the tap.
     */
    hideTrigger?: boolean;
}

interface IMapButtonState {
    isModalVisible: boolean;
    center: IMapPosition;
}

/**
 * Opens the address in a new tab on google maps
 * @example
 * <MapButton mark={mark}>Open in maps</MapButton>
 */
export class MapButton extends Component<IMapButtonProps, IMapButtonState> {
    static defaultProps = {
        modalTitle: "Location",
        isOpenWithMap: false,
    };

    state: Readonly<IMapButtonState> = {
        isModalVisible: false,
        center: this.props.mark ? { ...this.props.mark.position } : { lat: 0, lng: 0 },
    };

    componentDidMount = () => {
        this.setState({ isModalVisible: this.props.isOpenWithMap });
    };

    private handleModalClose = () => {
        this.setState({
            isModalVisible: false,
        });
        this.props.onClose?.();
    };

    /** Opens the map modal imperatively (used with `hideTrigger`). */
    public openModal = () => {
        void this.handleButtonClick();
    };

    private handleButtonClick = async () => {
        let newCenter: IMapPosition = {
            ...(this.props.mark ? this.props.mark.position : this.state.center),
        };
        if (newCenter.lat === 0 && newCenter.lng === 0) {
            newCenter = await getMapPositionFromDeviceWithFallback(
                this.props.defaultMapStartPosition,
            );
        }

        this.setState({
            isModalVisible: true,
            center: newCenter,
        });
    };

    private handleMarkSet = (newPosition: IMapPosition) => {
        const { onMarkSet } = this.props;
        onMarkSet && onMarkSet(newPosition);
    };

    private handleMarkClear = () => {
        const { onMarkClear } = this.props;
        onMarkClear && onMarkClear();
    };

    render() {
        const {
            mark,
            readOnly,
            modalTitle,
            isOpenWithMap,
            entityName,
            defaultMapStartPosition,
            onMarkClear,
            onMarkSet,
            hideTrigger,
            ...otherProps
        } = this.props;
        const { isModalVisible, center } = this.state;

        return (
            <>
                {!hideTrigger && (
                    <BdsButton
                        testid={otherProps["data-testid"]}
                        displayType="tertiary"
                        ariaLabel="Map pin"
                        icon={<BdsIconMapPin />}
                        onClick={async (e) => {
                            await this.handleButtonClick();
                            otherProps.onClick?.(e);
                        }}
                        className={otherProps.className}
                    />
                )}
                <BTModal
                    data-testid="btModalMapButton"
                    className="MapModal"
                    beforeClose={this.handleModalClose}
                    visible={isModalVisible}
                    setPageTitle={false}
                    width="670px"
                    removeBodyPadding
                    useModalLayout
                    disableAnimation // Disables the modal animation so that the map renders properly on load
                >
                    <BTModalLayout
                        title={modalTitle}
                        modalConfig={{ beforeClose: this.handleModalClose, parentRoute: "" }}
                    >
                        <MapInput
                            center={center}
                            zoomLevel={14}
                            mark={mark}
                            readOnly={readOnly}
                            entityName={entityName}
                            onMarkSet={this.handleMarkSet}
                            onMarkClear={this.handleMarkClear}
                        />
                    </BTModalLayout>
                </BTModal>
            </>
        );
    }
}
